import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos información personal que usted nos proporciona directamente, como su nombre, dirección de correo electrónico y datos de pago al realizar una compra.'
  }, {
    heading: 'Uso de la Información',
    text: 'Utilizamos su información para procesar transacciones, enviar el producto digital adquirido, y comunicarnos con usted sobre su compra. No compartimos su información con terceros sin su consentimiento.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad para proteger su información personal. Los pagos se procesan a través de plataformas seguras con encriptación SSL.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio puede utilizar cookies para mejorar su experiencia de navegación y analizar el tráfico del sitio.'
  }, {
    heading: 'Sus Derechos',
    text: 'Usted tiene derecho a acceder, rectificar o eliminar su información personal en cualquier momento contactándonos directamente.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    text: '12Metabolismo es un producto digital (ebook) que se entrega de forma inmediata tras la confirmación del pago. El contenido es para uso personal y no puede ser redistribuido.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Ofrecemos una garantía de satisfacción. Si no está conforme con el producto, puede solicitar un reembolso dentro de los primeros 7 días posteriores a la compra.'
  }, {
    heading: 'Descargo de Responsabilidad',
    text: 'El contenido de 12Metabolismo es informativo y educativo. No sustituye el consejo médico profesional. Consulte a su médico antes de iniciar cualquier programa de salud.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido, incluyendo textos, imágenes y metodología, está protegido por derechos de autor. Queda prohibida su reproducción sin autorización.'
  }, {
    heading: 'Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Los cambios serán efectivos inmediatamente después de su publicación.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-[#1A1A1A]" data-id="element-56">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-57">
          <p className="text-gray-400 text-sm font-sans" data-id="element-58">
            © {new Date().getFullYear()}{' '}
            <span className="text-[#2D8C4E] font-semibold" data-id="element-59">12</span>Metabolismo.
            Todos los derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-60">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-400 text-sm font-sans hover:text-white transition-colors duration-200" data-id="element-61">
              Política de Privacidad
            </button>
            <span className="text-gray-600" data-id="element-62">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-400 text-sm font-sans hover:text-white transition-colors duration-200" data-id="element-63">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-gray-500 text-xs font-sans" data-id="element-64">
            contacto@12metabolismo.com
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-65">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-66">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-67">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-[#F0FDF4]" data-id="element-68">
                <h3 className="text-xl font-bold text-[#1A1A1A] font-serif" data-id="element-69">
                  {content.title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#2D8C4E]/10 transition-colors duration-200 text-gray-500 hover:text-[#2D8C4E]" aria-label="Cerrar" data-id="element-70">
                  <XIcon size={18} data-id="element-71" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[60vh] space-y-6" data-id="element-72">
                {content.sections.map((section, i) => <div key={i} data-id="element-73">
                    <h4 className="font-bold text-[#1A1A1A] mb-2 font-serif text-lg" data-id="element-74">
                      {section.heading}
                    </h4>
                    <p className="text-gray-600 font-sans leading-relaxed text-sm" data-id="element-75">
                      {section.text}
                    </p>
                  </div>)}

                {/* Contact info */}
                <div className="pt-4 border-t border-gray-100" data-id="element-76">
                  <h4 className="font-bold text-[#1A1A1A] mb-2 font-serif text-lg" data-id="element-77">
                    Contacto
                  </h4>
                  <p className="text-gray-600 font-sans text-sm" data-id="element-78">
                    Si tienes preguntas, escríbenos a:{' '}
                    <a href="mailto:contacto@12metabolismo.com" className="text-[#2D8C4E] font-medium hover:underline" data-id="element-79">
                      contacto@12metabolismo.com
                    </a>
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50" data-id="element-80">
                <button onClick={() => setActiveModal(null)} className="w-full py-2.5 text-sm font-semibold text-[#2D8C4E] bg-[#F0FDF4] rounded-lg hover:bg-[#2D8C4E] hover:text-white transition-colors duration-200" data-id="element-81">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}